import { OrderApiResponseStateService } from './../../store/state/order/orderv2/order-api-response/order-api-response-state.service';
import { inject, Injectable } from "@angular/core";
import { OrderSetupStateService } from "../../store/state/order/orderv2/workflows/order-setup/order-setup-state.service";
import { ServiceInformationCollectionStateService } from "../../store/state/order/orderv2/workflows/service-info-collection/service-info-collection-state.service";
import { InternetPackageSelectionStateService } from "../../store/state/order/orderv2/workflows/internet/internet-package-selection/internet-package-selection-state.service";
import { CreditCheckStateService } from "../../store/state/order/orderv2/workflows/credit-check/credit-check-state.service";
import { OrderStateServiceV2 } from "../../store/state/order/orderv2/order-state.service";
import { OrderCustomizationsStateService } from "../../store/state/order/orderv2/workflows/order-customization/order-customization.state.service";
import { InternetProductResponseService } from "../../store/state/order/orderv2/order-api-response/internet-pd-response.service";
import { WirelessProductResponseService } from "../../store/state/order/orderv2/order-api-response/wireless-pd-response.service";
import { OrderFlowType, ProductType } from "../../models/order/order.model";
import { WirelessFlowType } from "../../models/order/workflow/wireless/wireless-flow.model";
import { WirelessOrderService } from "./wireless-order.service";
import { OrderCustomizationPayload, OrderWirelessCustomizationPayload } from "../../models/order/workflow/order-customization.model";
import { VideoPackageSelectionStateService } from "../../store/state/order/orderv2/workflows/video/video-package-selection/video-package-selection-state.service";
import { VoicePackageSelectionStateService } from '../../store/state/order/orderv2/workflows/voice/voice-package-selection/voice-package-selection-state.service';
import { AlternateShippingAddressStateService } from '../../store/state/order/orderv2/workflows/alternate-shipping-address/alternate-shipping-address.state.service';
import { ServiceLevelSelectionStateService } from '../../store/state/order/orderv2/workflows/wireless/service-level-selection/service-level-selection.state.service';
import { EmailValidationStateService } from '../../store/state/order/orderv2/workflows/video/email-validation/email-validation.state.service';
import { PreviousAddressStateService } from '../../store/state/order/orderv2/workflows/previous-address/previous-address-state.service';
import { OrderTypeSelection } from './order.service';

@Injectable({
    providedIn: 'root'
})
export class OrderPayloadHelperService {
    orderSetupStateService = inject(OrderSetupStateService);
    OrderApiResponseStateService = inject(OrderApiResponseStateService);
    internetPackageSelectionStateService = inject(InternetPackageSelectionStateService);
    videoPackageSelectionStateService = inject(VideoPackageSelectionStateService);
    voicePackageSelectionStateService = inject(VoicePackageSelectionStateService);
    serviceInformationCollectionStateService = inject(ServiceInformationCollectionStateService);
    creditCheckStateService = inject(CreditCheckStateService);
    emailValidationStateService = inject(EmailValidationStateService);
    alternateShippingAddressStateService = inject(AlternateShippingAddressStateService);
    previousAddressStateService = inject(PreviousAddressStateService);
    serviceLevelSelectionStateService = inject(ServiceLevelSelectionStateService);
    orderStateService = inject(OrderStateServiceV2);
    orderCustomizationStateService = inject(OrderCustomizationsStateService);
    internetProductResponseService = inject(InternetProductResponseService);
    wirelessProductResponseService = inject(WirelessProductResponseService);
    wirelessOrderService = inject(WirelessOrderService);

    getOrderBasicInfo() {
        const {OrderNumber, ConnId, CustomerId, SalesRepID} = this.orderSetupStateService.preOrderData() ?? {};

        const selectedDealerCode = this.orderSetupStateService.getSelectedDealerCode();

        const OrderType = this.orderSetupStateService.getOrderType() as OrderTypeSelection;

        return {
            OrderNumber,
            ConnId,
            CustomerId,
            SalesRepID,
            OrderType,
            selectedDealerCode
        };
    }

    getAvailableServicesSQPayloadInfo() {
        const referenceNumberForm = this.orderSetupStateService.state().referenceNumbers;
        const orderOptionsForm = this.orderSetupStateService.state().orderOptions;
        const salesInformationForm = this.orderSetupStateService.state().salesInformation;

        const selectedStore = this.orderSetupStateService.stores().find(store => store.DealerCode === salesInformationForm?.store);
        const storeId = selectedStore?.StoreId;

        const selectedRetailer = this.orderSetupStateService.retailers().find(retailer => retailer.StoreName === salesInformationForm?.retailer);
        const directvId = selectedRetailer?.RetailerNumber ?? '';

        return {
            referenceNumberForm,
            orderOptionsForm,
            salesInformationForm,
            storeId,
            directvId
        };
    }

    getCustomer() {
        return this.OrderApiResponseStateService.customer();
    }

    getInternetPackageSelectionStateData() {
        return this.internetPackageSelectionStateService.state();
    }

    getCreditCheckStateData() {
        return this.creditCheckStateService.state();
    }

    getEmailValidationStateData() {
        return this.emailValidationStateService.state();
    }

    getAlternateShippingAddressStateData() {
        return this.alternateShippingAddressStateService.state();
    }

    getPreviousAddressStateData() {
        return this.previousAddressStateService.state() || null;
    }

    getServiceLevelSelectionStateData() {
        return this.serviceLevelSelectionStateService.state();
    }

    getSelectedPlanCode(type: OrderFlowType): string | undefined {
        if (type === OrderFlowType.Internet) {
            return this.internetPackageSelectionStateService.state()?.selectedPlanCode;
        }

        if (type === OrderFlowType.Video) {
            return this.videoPackageSelectionStateService.state()?.selectedPlanCode;
        }

        if (type === OrderFlowType.Voice) {
            return this.voicePackageSelectionStateService.state()?.selectedPlanCode;
        }

        if (type === OrderFlowType.Wireless) {
            return this.orderStateService.order()?.wirelessFlow?.wirelessSetup?.selectedCustomerCode;
        }

        return undefined;
    }

    getServiceInformationCollectionStateData() {
        return this.serviceInformationCollectionStateService.state();
    }

    getInternetProductDetailsCustomizationInfo(): OrderCustomizationPayload | null {
        const componentCode = this.getSelectedPlanCode(OrderFlowType.Internet);
        if (!componentCode) return {
            Category: ProductType.Internet,
            Code: ''
        };
        const internetPayload: OrderCustomizationPayload = {
            Category: ProductType.Internet,
            Code: componentCode || '',
        };

        //We only add customization if creditcheck is done
        const internetCustomizations = this.orderCustomizationStateService.buildInternetCustomizationPayload();
        if (internetCustomizations) {
            internetPayload.Customization = internetCustomizations;
        }

        return internetPayload;
    }

    getVideoProductDetailsCustomizationInfo(): OrderCustomizationPayload | null {
        const componentCode = this.getSelectedPlanCode(OrderFlowType.Video);
        if (!componentCode) return {
            Category: ProductType.Video,
            Code: ''
        };
        const videoPayload: OrderCustomizationPayload = {
            Category: ProductType.Video,
            Code: componentCode || '',
        };

        //We only add customization if creditcheck is done
        const videoCustomizations = this.orderCustomizationStateService.buildVideoCustomizationPayload();
        if (videoCustomizations) {
            videoPayload.Customization = videoCustomizations;
        }

        return videoPayload;
    }

    getVoiceProductDetailsCustomizationInfo(): OrderCustomizationPayload | null {
        const componentCode = this.getSelectedPlanCode(OrderFlowType.Voice);
        if (!componentCode) return {
            Category: ProductType.Voice,
            Code: ''
        };
        const voicePayload: OrderCustomizationPayload = {
            Category: ProductType.Voice,
            Code: componentCode || '',
        };

        const voiceCustomizations = this.orderCustomizationStateService.buildVoiceCustomizationPayload();
        if (voiceCustomizations) {
            voicePayload.Customization = voiceCustomizations;
        }

        return voicePayload;
    }

    getWirelessProductDetailsCustomizationInfo(forCreditCheck = false): OrderWirelessCustomizationPayload | null {
        const wirelessSetup = this.orderStateService.order()?.wirelessFlow?.wirelessSetup;
        // For credit check (CC / CC Plus) only: existing wireless account not authenticated
        // send an empty wireless stub instead of the real wireless info.
        if (forCreditCheck
            && wirelessSetup?.selectedCustomerCode === WirelessFlowType.EXISTING
            && !wirelessSetup?.accountAuthenticationComplete) {
            return {
                Category: ProductType.Wireless,
                Code: ''
            } as OrderWirelessCustomizationPayload;
        }
        const wirelessComponentCode = this.getSelectedPlanCode(OrderFlowType.Wireless);
        if (!wirelessComponentCode) return {
            Category: ProductType.Wireless,
            Code: '',
            ShippingMethod: '',
            SigCapture: '',
            IgnoreWirelessValidation: true
        } as OrderWirelessCustomizationPayload;
        const portIn = this.getCreditCheckStateData()?.customerPreferences?.portingNumber === 'true' ? true : false;
        const numberOfLines = this.getCreditCheckStateData()?.customerPreferences?.requestedLines || 0;
        const serviceLevel = this.getServiceLevelSelectionStateData()?.serviceLevel;
        const signatureRequired = this.getServiceLevelSelectionStateData()?.signatureRequired;
        const wirelessPayload: OrderWirelessCustomizationPayload = {
            Category: ProductType.Wireless,
            Code: wirelessComponentCode || '',
            ShippingMethod: serviceLevel || '',
            SigCapture: signatureRequired || '',
            Lines: [],
            NumberOfLinesRequested: numberOfLines,
            PortIn: portIn
        };

        const wirelessCustomizations = this.orderCustomizationStateService.buildWirelessCustomizationPayload();
        if (wirelessCustomizations) {
            wirelessPayload.Customization = wirelessCustomizations;
        }

        const lines = this.wirelessOrderService.getAllLinesRequestPayload();
        if (lines.length > 0) {
            wirelessPayload.Lines = lines;
        }

        return wirelessPayload
    }

    getWirelessProductPlusDetailsCustomizationInfo(line?: any) {
        const CustomizationInfo = [];
        CustomizationInfo.push({
            Category: "Wireless",
            Code: this.orderStateService.order()?.wirelessFlow?.wirelessSetup?.selectedCustomerCode || '',
            Line: line
        })
        return CustomizationInfo;
    }

    getCustomizationInfo(particularFlow?: OrderFlowType, forCreditCheck = false) {
        const customizations = [];
        const order = this.orderStateService.order();
        if (!order) return customizations;
        const initiatedFlows = this.orderStateService.serviceStatuses();
        for (const flow of initiatedFlows) {
            if (flow.key === 'INTERNET' && (!particularFlow || particularFlow === OrderFlowType.Internet)) {
                const internetPayload = this.getInternetProductDetailsCustomizationInfo();
                if (internetPayload) {
                    customizations.push(internetPayload);
                }
            }
            if (flow.key === 'WIRELESS' && (!particularFlow || particularFlow === OrderFlowType.Wireless)) {
                const wirelessPayload = this.getWirelessProductDetailsCustomizationInfo(forCreditCheck);
                if (wirelessPayload) {
                    customizations.push(wirelessPayload);
                }
            }
            if (flow.key === 'VIDEO' && (!particularFlow || particularFlow === OrderFlowType.Video)) {
                const videoPayload = this.getVideoProductDetailsCustomizationInfo();
                if (videoPayload) {
                    customizations.push(videoPayload);
                }
            }
            if (flow.key === 'VOICE' && (!particularFlow || particularFlow === OrderFlowType.Voice)) {
                const voicePayload = this.getVoiceProductDetailsCustomizationInfo();
                if (voicePayload) {
                    customizations.push(voicePayload);
                }
            }
        }
        // CC / CC Plus: only send components that actually have a code.
        return forCreditCheck ? customizations.filter(c => (c as any)?.Code) : customizations;
    }

}